import { cast, types, } from 'mobx-state-tree';
import { Lead } from './LeadModel';
import { Asset } from './AssetModel';
import { Certification, Course, Instructor, CourseProgress, } from './CourseModel';
import { Customer, LearningBoard, Payment, } from './CustomerModel';
import { Document, DocumentProgress, } from './DocumentModel';
import { Repository } from './RepositoryModel';
import { Organization, User, UserOrganization, ClientAi, } from './OrganizationModel';
import { Category, Membership, Portal, PortalTemplate, Member, } from './PortalModel';
import { Question, Questionnaire, } from './QuestionnaireModel';
import { EngagementFlow } from './EngagementFlowModel';
import { EmailTemplate } from './EmailTemplateModel';
import { Hub } from './HubModel';
import { Item } from './ItemModel';
import { ItemProgress } from './ReportsModel';
import { Kicker } from './KickerModel';
import { Library } from './LibraryModel';
import { LibraryShare } from './LibraryShareModel';
import { Meeting, Registration } from './MeetingModel';
import { Microcast, MicrocastProgress } from './MicrocastModel';
import { Post, Topic } from './DiscussionModel';
import { Program, ProgramProgress } from './ProgramModel';
import { Product } from './ProductModel';
import { EcommerceCategory } from './EcommerceCategoryModel';
import { WebPage } from './WebPageModel';
import { ShopifyConfig } from './ShopifyConfigModel';
import { SyncStateType, JsonType } from './Schema';
/**
 * Cached list model holds references to items that are retrieved from the API.
 */
const listableTypes = types.union(Asset, Category, Certification, ClientAi, Course, CourseProgress, Customer, Document, DocumentProgress, EmailTemplate, EngagementFlow, Hub, Instructor, Item, ItemProgress, LearningBoard, Lead, Library, LibraryShare, Meeting, Member, Membership, Microcast, MicrocastProgress, Organization, Payment, Portal, Product, EcommerceCategory, WebPage, Kicker, Post, Program, ProgramProgress, Question, Questionnaire, Registration, Repository, ShopifyConfig, Topic, User, UserOrganization);
export const PageSet = types.model({
    // tricky here - its an array of pages, each page is an array of references
    pages: types.array(types.array(types.reference(listableTypes))),
    itemsPerPage: types.number,
    loadCount: types.number, // number of items loaded into this set - used when adding items
})
    .actions((self) => ({
    getPage(page) {
        // make sure we've requested enough items
        // self.requestedCount = (page + 1) * self.itemsPerPage;
        // create the pages if needed
        while (self.pages.length <= page) {
            self.pages.push(cast([]));
        }
        return self.pages[page];
    },
    loadPages(items) {
        // loads any new items into the pages, creating new pages as needed
        // total number of pages
        const totalPages = Math.ceil(items.length / self.itemsPerPage);
        const currentPage = Math.floor(self.loadCount / self.itemsPerPage);
        for (let i = currentPage; i < totalPages; i += 1) {
            const page = self.getPage(i);
            const newItems = items.slice(i * self.itemsPerPage, (i + 1) * self.itemsPerPage);
            console.log(`Loading page ${i} with ${newItems.length} items`);
            for (let j = page.length; j < newItems.length; j += 1) {
                page.push(newItems[j]);
            }
        }
        // update the count for next time
        self.loadCount = items.length;
    },
    removeItem(item) {
        self.pages.forEach((page) => { page.remove(item); });
    },
    reset() {
        self.pages.forEach((page) => { page.clear(); });
        // self.pages.clear();
        self.loadCount = 0;
    },
}))
    .views((self) => ({
    hasPage(page) {
        return self.pages.length > page && self.pages[page].length > 0;
    },
}));
export const CachedListModel = types.model({
    id: types.identifier,
    modelName: types.frozen(types.string),
    syncState: SyncStateType,
    isOrganizationScoped: types.boolean,
    items: types.array(types.safeReference(listableTypes)), // this is all the items loaded
    pageSets: types.map(PageSet),
    nextToken: types.maybeNull(types.string),
    filter: types.frozen(JsonType),
    sort: types.frozen(JsonType),
    totalCount: types.number, // total items as returned from search query - not avaible in all cases
    // Server-side aggregate roll-ups for itemProgress lists (totalRows/completedCount/etc).
    // Stored as opaque JSON so this model stays generic across list shapes.
    aggregates: types.maybeNull(types.frozen(JsonType)),
    requestedCount: types.number, // number of items requested from the UI for display.
    moreItems: types.boolean,
    loadError: types.optional(types.string, ''),
})
    .actions((self) => ({
    setRequestedCount(count) {
        self.requestedCount = count;
    },
    setLoadError(message) {
        self.loadError = message;
    },
    setAggregates(aggregates) {
        self.aggregates = aggregates;
    },
    getPageSet(itemsPerPage) {
        // create the page if needed
        if (!self.pageSets.get(itemsPerPage)) {
            const newSet = PageSet.create({
                itemsPerPage,
                pages: [],
                loadCount: 0,
            });
            newSet.loadPages(self.items);
            self.pageSets.set(itemsPerPage, newSet);
        }
        return self.pageSets.get(itemsPerPage);
    },
    getPage(page, itemsPerPage) {
        // make sure we've requested enough items
        return self.getPageSet(itemsPerPage).getPage(page);
    },
    appendItems(newItems) {
        newItems.forEach((newItem) => {
            if (self.items.find((item) => item.id === newItem.id)) {
                console.log('Item already in list', newItem);
                return;
            }
            self.items.push(newItem);
        });
        Array.from(self.pageSets.values()).map((pageSet) => (pageSet.loadPages(self.items)));
    },
    reset() {
        self.syncState = 'stubbed';
        self.items.clear(); // = cast([]);
        self.pageSets.forEach((pageSet) => pageSet.reset());
        // self.pageSets.clear();
        self.nextToken = null;
        self.totalCount = -1;
        self.aggregates = null;
        self.loadError = '';
        // self.requestedCount = 0;
        self.moreItems = true;
    },
    getItems() {
        self.setRequestedCount(1000);
        // todo: trigger the load
        return self.items;
    },
    removeItem(item) {
        self.items.remove(item);
        self.pageSets.forEach((pageSet) => { pageSet.removeItem(item); });
    },
}))
    .views((self) => ({
    hasMorePages(itemsPerPage, page) {
        // console.log(`${self.id} has pages`, getSnapshot(self));
        return self.moreItems || self.getPageSet(itemsPerPage).hasPage(page);
    },
    hasSearch() {
        // this should be moved to microcasting.schema.js and accessed through Schema.ts
        return [
            'asset',
            'certificate',
            'certification',
            'course',
            'customer',
            'document',
            'ecommerceCategory',
            'email',
            'engagementFlow',
            'hub',
            'instructor',
            'itemProgress',
            'lead',
            'meeting',
            /* meetingRegistration: not indexed (operational data); retrieved via Dynamo get/list + dedicated resolvers */
            'member', // - delay in getting updates may cause issues in admin, but querying is slow otherwise
            'membership',
            'product',
            /* 'sku' - use list, not search; not indexed in KB for now */
            'webPage',
            // organization: searchable so the search box shows, but resolveListSearchPath keeps
            // the default (no-text) listing on Dynamo listOrganizations so deletion status is
            // always current; only an actual text query routes to pgvector search.
            'organization',
            /* 'portal', portal is not indexing correctly - error says more than 1000 fields */
            'program',
            // 'payment',
            'questionnaire',
            'question',
            'user',
            'microcast',
            'kicker',
            // discussionPost/discussionTopic load via pgvector search (filtered by topicID +
            // post_status server-side). NOTE: this path is eventually consistent — posts are
            // indexed from the DynamoDB stream, so newly created/edited/moderated/deleted posts
            // can lag a second or two before they appear on refetch. DiscussionGroup compensates
            // with optimistic local updates on create. Portal callers only ever see approved posts.
            'discussionPost',
            'discussionTopic',
        ].includes(self.modelName);
    },
    useCognitoAuthMode() {
        // this should be moved to microcasting.schema.js and accessed through Schema.ts
        return [
            'itemProgress',
            'documentProgress',
        ].includes(self.modelName);
    },
}));
export { Category, EmailTemplate, EngagementFlow, ItemProgress, Lead, Library, LibraryShare, Membership, Portal, Kicker, Topic, Post, PortalTemplate, Question, Questionnaire, Repository, };
//# sourceMappingURL=PageSetModel.js.map